// src/lib/canvas/changeCard.ts
//
// The change card (spec 2026-10-14): one list of changes Symphony proposes,
// previewed dashed on the page, applied on one Approve with one Undo. These
// types mirror supabase/functions/symphony-agent/changeSet.ts.

import type { Task } from '@/types/task'
import { lineDropUpdates } from '@/lib/planning/v2/planV2'
import { timingRemoval } from '@/lib/planning/planActions'
import { localYmd } from '@/lib/cadence/config'
import { STATUS_LABEL, type CellValue, type ColumnKind, type OptionStatus } from '@/types/choice'
import { formatCents } from '@/lib/choices/price'
import { LOG_KIND_LABEL, type LogKind } from '@/types/goalLog'
import { weekdayLabel } from '@/lib/choices/log'

export type TaskOpKind = 'place' | 'unplace' | 'add' | 'someday' | 'assign' | 'done' | 'merge' | 'talk'
/** Routine ops (stage 3, spec 2026-10-14-routines-talk-first). skip/move/who
 *  change ONE day's occurrence ("this week only"); change/rest/add change the
 *  routine itself ("from now on"). */
export type RoutineOpKind = 'routine_skip' | 'routine_move' | 'routine_who' | 'routine_change' | 'routine_rest' | 'routine_add'
/** Plan ops (stage 5, Month / Season / Year): goals on those lists. Month and
 *  season goals are tasks committed to a period; year goals are `goals` rows
 *  (itemKind). Done, merge and someday for month/season goals reuse the task
 *  ops above. */
export type PlanOpKind = 'plan_add' | 'plan_carry' | 'plan_let_go' | 'plan_done'
/** Choice ops (spec 2026-10-22-choices, "Chat"): a choice on a goal, its
 *  options, their prices and values, the columns and the work, the decision. */
export type ChoiceOpKind = 'choice_add' | 'option_add' | 'option_status' | 'option_set' | 'price_line' | 'column_add' | 'work_set' | 'choice_choose' | 'choice_reopen' | 'follow_up'
  | EventOpKind
/** Log ops (spec 2026-10-22-choice-log, "Chat"): a call or visit that happened
 *  (event_log) or one planned ahead (event_plan), about a goal or one of its
 *  choices' options. Never a status change by itself: that is its own row. */
export type EventOpKind = 'event_log' | 'event_plan'
export type ChangeOpKind = TaskOpKind | RoutineOpKind | PlanOpKind | ChoiceOpKind
export type PlanLevel = 'year' | 'season' | 'month'

export type DayKey = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat'

export interface ChangeOp {
  /** Per-item timing for the proposal-only first-activities introduction. */
  capturePlacement?: 'inbox' | 'week' | 'day'
  captureWeekStart?: string
  key: string
  op: ChangeOpKind
  taskId?: string
  /** The task's or routine's current title (resolved server-side), or the new
   *  task's / routine's name for add and routine_add. */
  title: string
  /** YYYY-MM-DD. For routine_skip/move/who: the occurrence's own day. */
  date?: string
  /** HH:MM, 24-hour. routine_move: the new time; routine_change/add: the routine's time. */
  time?: string
  assignee?: string
  assigneeName?: string
  goalId?: string
  /** The task's (or, for routine_change/rest, the routine's) updated_at when Symphony read it. */
  seen?: string
  /** merge: the duplicate that closes into taskId. */
  fromTaskId?: string
  fromTitle?: string
  fromSeen?: string
  /** talk: what to talk about, or with whom ("with Iris"). */
  note?: string
  routineId?: string
  /** routine_move: YYYY-MM-DD the occurrence moves to. */
  toDate?: string
  /** routine_change: the new name. */
  name?: string
  /** routine_change: true = any time of day (clear the time). */
  clearTime?: boolean
  /** routine_change/add: the weekly days. */
  days?: DayKey[]
  /** routine_change/add: who does it, every week. */
  assignees?: string[]
  assigneeNames?: string[]
  /** routine_add/change: shared Today and Week visibility. */
  showOnTimeline?: boolean
  /** routine_rest: YYYY-MM-DD it wakes; none = rest with no date. */
  until?: string
  /** routine_add, add, plan_add: the space (Work, Family or Personal). */
  context?: 'family' | 'personal' | 'work'
  /** add, plan_add: the life area (goal_areas row) it is filed under, and its name. */
  areaId?: string
  areaName?: string
  /** Any op: what Symphony needs to know before this row can be written.
   *  Shown on the row; Apply writes the other rows and not this one. */
  question?: string
  /** plan ops: which list. */
  level?: PlanLevel
  /** plan ops: YYYY-MM-DD start of the period the item is on now (or, for
   *  plan_add, the period it is added to). A year is YYYY-01-01. */
  periodStart?: string
  /** plan ops: the period's name as the card says it ("November", "Fall", "2027"). */
  periodLabel?: string
  /** plan_carry: the period it goes to, and its name. */
  toPeriodStart?: string
  toPeriodLabel?: string
  /** plan_carry: true when the target is the very next period (CARRY), false
   *  when it skips ahead (MOVE … to January). */
  toNext?: boolean
  /** plan ops: the item, a task (month/season goal) or a year goal row. */
  itemId?: string
  itemKind?: 'task' | 'goal'
  /** plan_add / plan_carry: the parent goal it serves (season item for a month
   *  goal, year goal for a season goal), and its title. */
  parentId?: string
  parentTitle?: string
  /** plan_carry: the new wording, if the person reworded it. */
  newTitle?: string
  // Choice ops. Every one except choice_add names its choice by exactly one of
  // choiceId (an existing choice) or choiceKey (the `key` of a choice_add
  // earlier in this set); option-level ops name their option the same way
  // (optionId, or optionKey of an option_add). `seen` is the choice's
  // updated_at when Symphony read it; `note` is the choose reason or a price
  // line's note; `date` is a follow-up's day.
  choiceId?: string
  choiceKey?: string
  choiceTitle?: string
  /** choice_add: the goal it is under, a goal task (month / season goal) or a
   *  year goal row (`goalId`, above). */
  goalTaskId?: string
  goalTitle?: string
  optionId?: string
  optionKey?: string
  optionName?: string
  /** option_add: an existing contact to link; else `phone` makes one. */
  contactId?: string
  phone?: string
  /** option_status: the new status. */
  status?: OptionStatus
  /** option_set / column_add: the column (column_add carries the id the parser
   *  assigned, which later ops in the set use). */
  columnId?: string
  columnLabel?: string
  columnKind?: ColumnKind
  /** option_set: the value, already typed (money cents, ISO date, yes/no, text). */
  value?: CellValue
  /** price_line: the pieces it covers; work_set: [the piece's id]. */
  workIds?: string[]
  workLabels?: string[]
  /** price_line: integer cents. */
  amountCents?: number
  /** work_set: what the piece of work is. */
  description?: string
  // Log ops. About exactly one of a goal (goalTaskId / goalId) or an option
  // (named as the option-level choice ops name theirs). `date` is the day it
  // happened or will, `time` its HH:MM if any.
  logKind?: LogKind
  /** event_log / event_plan: what happened, or what it is for. */
  summary?: string
}

/** Which heading a row sits under on the card. */
export type OpScope = 'task' | 'week' | 'lasting' | 'plan' | 'choice'
export const CHOICE_OPS: ReadonlySet<ChangeOpKind> = new Set<ChoiceOpKind>(['choice_add', 'option_add', 'option_status', 'option_set', 'price_line', 'column_add', 'work_set', 'choice_choose', 'choice_reopen', 'follow_up', 'event_log', 'event_plan'])
export const isChoiceOp = (op: ChangeOp) => CHOICE_OPS.has(op.op)
export const isEventOp = (op: ChangeOp) => op.op === 'event_log' || op.op === 'event_plan'
/** A log op about the goal itself, not an option of one of its choices. */
export const isGoalEvent = (op: ChangeOp) => isEventOp(op) && !op.choiceId && !op.choiceKey
export function opScope(op: ChangeOp): OpScope {
  if (isChoiceOp(op)) return 'choice'
  if (op.op.startsWith('plan_')) return 'plan'
  if (op.op === 'routine_skip' || op.op === 'routine_move' || op.op === 'routine_who') return 'week'
  if (op.op.startsWith('routine_')) return 'lasting'
  return 'task'
}
export const SCOPE_HEADING: Record<OpScope, string> = {
  task: 'Tasks',
  week: 'Routines · this week only',
  lasting: 'Routines · from now on',
  plan: 'Goals',
  choice: 'Choices',
}

/** The card heading a choice op sits under: its goal and the choice
 *  ("Redo the bathrooms · Contractor"). */
export function choiceHeading(op: ChangeOp): string {
  if (isGoalEvent(op)) return op.goalTitle ?? op.title
  const choice = op.choiceTitle ?? op.title
  return op.goalTitle ? `${op.goalTitle} · ${choice}` : choice
}

/** The card heading a plan op sits under: the list it changes ("November",
 *  "October", "Fall"). A carry is listed under where it lands. */
export function planHeading(op: ChangeOp): string {
  if (op.op === 'plan_carry') return op.toPeriodLabel ?? 'Goals'
  return op.periodLabel ?? 'Goals'
}

export interface ChangeSet {
  key: string
  title: string
  ops: ChangeOp[]
  /** YYYY-MM-DD: the week being planned (read_week's start). An add with no
   *  day lands in it. */
  weekStart?: string
  /** A line under the rows, e.g. what a guided step left out. */
  note?: string
}

/** A row waiting on the person's answer: shown, never written by Apply. */
export const isQuestion = (op: ChangeOp) => !!op.question
/** The rows Apply writes. */
export const toApply = (ops: readonly ChangeOp[]) => ops.filter((op) => !isQuestion(op))

/** What became of the latest card, told to the next turn: Symphony's history
 *  holds only the words, so without this it re-proposes changes already saved
 *  (or ones the person dismissed). */
export interface ChangeCardNote {
  /** Complete proposal-only onboarding state, preserved through corrections. */
  draft?: ChangeSet
  state: 'pending' | 'approved' | 'dismissed'
  title: string
  ops: { op: ChangeOpKind; title: string; where: string; taskId?: string; date?: string; time?: string; question?: string }[]
  /** ISO time the card reached this state. */
  at: string
}

export function changeCardNote(set: ChangeSet, state: ChangeCardNote['state'], now = new Date()): ChangeCardNote {
  return { state, title: set.title, ops: set.ops.map((op) => ({ op: op.op, title: op.title, where: opRow(op, now).where, ...(op.taskId ? {taskId:op.taskId} : {}), ...(op.date ? {date:op.date} : {}), ...(op.time ? {time:op.time} : {}), ...(op.question ? {question:op.question} : {}) })), at: now.toISOString() }
}

/** Tasks with their steps (and Today-group members) laid out flat. The app
 *  holds steps inside their parent's `subtasks`; Symphony reads them as
 *  ordinary tasks, so a set may name one. */
export function withSteps(tasks: readonly Task[]): Task[] {
  return tasks.flatMap((t) => [t, ...(t.subtasks ?? [])])
}

/** The local moment an op names: its date at its time, or at midnight. */
export function opWhen(op: ChangeOp): Date | undefined {
  if (!op.date) return undefined
  const [y, m, d] = op.date.split('-').map(Number)
  const [h, min] = op.time ? op.time.split(':').map(Number) : [0, 0]
  return new Date(y, m - 1, d, h, min)
}

function clock(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const hour = h % 12 === 0 ? 12 : h % 12
  return `${hour}:${String(m).padStart(2, '0')}`
}

function ymdDate(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

function dayName(ymd: string, today: Date): string {
  return ymd === localYmd(today) ? 'Today' : ymdDate(ymd).toLocaleDateString('en-US', { weekday: 'short' })
}

/** "Thu 15": an occurrence's day, with its date so "this week only" reads true. */
function dayAndDate(ymd: string): string {
  const d = ymdDate(ymd)
  return `${d.toLocaleDateString('en-US', { weekday: 'short' })} ${d.getDate()}`
}

function dayWords(op: ChangeOp, today: Date): string {
  const day = dayName(op.date!, today)
  return op.time ? `${day} ${clock(op.time)}` : day
}

const DAY_ORDER: DayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const DAY_SHORT: Record<DayKey, string> = { sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat' }

/** "Every day", "Mon–Thu", "Tue, Thu", "Sat". */
export function daysWords(days: readonly DayKey[]): string {
  const set = new Set(days)
  const sorted = DAY_ORDER.filter((d) => set.has(d))
  if (sorted.length === 7) return 'Every day'
  if (!sorted.length) return 'No day yet'
  const idx = sorted.map((d) => DAY_ORDER.indexOf(d))
  const run = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1)
  if (run && sorted.length >= 3) return `${DAY_SHORT[sorted[0]]}–${DAY_SHORT[sorted[sorted.length - 1]]}`
  return sorted.map((d) => DAY_SHORT[d]).join(', ')
}

const names = (list?: string[]) => (list?.length ? list.join(', ') : 'no one')

export type RowKind = 'MOVE' | 'ADD' | 'LATER' | 'WHO' | 'DONE' | 'MERGE' | 'TALK' | 'SKIP' | 'TIME' | 'DAYS' | 'NAME' | 'CHANGE' | 'REST' | 'NEW' | 'CARRY' | 'LET GO' | 'MET'
  | 'STATUS' | 'PRICE' | 'SET' | 'COLUMN' | 'WORK' | 'CHOOSE' | 'REOPEN' | 'FOLLOW UP' | 'LOG' | 'PLAN'

const KIND_WORD: Record<ColumnKind, string> = { text: 'text', money: 'money', date: 'date', yesno: 'yes/no' }

/** A value as the comparison shows it: $18,400, Dec 1, Yes / No. */
function cellWords(kind: ColumnKind | undefined, value: CellValue | undefined): string {
  if (value == null || value === '') return 'blank'
  if (kind === 'money' && typeof value === 'number') return formatCents(value)
  if (kind === 'yesno' && typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (kind === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return ymdDate(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
  return String(value)
}

/** "Fri Oct 30, 10:00": the date itself, not a weekday alone, for a day that
 *  may be outside this week. */
function datedWords(op: ChangeOp): string {
  return `${weekdayLabel(op.date!)}${op.time ? `, ${clock(op.time)}` : ''}`
}

/** A log's day: "Today" as other rows say it, else the date ("Tue Oct 13"),
 *  so an older log is not read as this week. */
function logDay(op: ChangeOp, today: Date): string {
  if (!op.date) return ''
  return op.date === localYmd(today) ? dayWords(op, today) : datedWords(op)
}

/** "Scher Remodeling · Phone call · Today": what it is about, the kind, the day. */
function eventTitle(op: ChangeOp, when: string): string {
  return [op.optionName ?? op.goalTitle ?? op.title, op.logKind ? LOG_KIND_LABEL[op.logKind] : null, when || null].filter(Boolean).join(' · ')
}

/** One card row: what kind of change, to what, and where it lands. */
/** "No day yet · Health": the life area an add is filed under, when it has one. */
const inArea = (op: ChangeOp, where: string) => (op.areaName ? `${where} · ${op.areaName}` : where)

export function opRow(op: ChangeOp, today: Date): { kind: RowKind; title: string; where: string } {
  switch (op.op) {
    case 'place': return { kind: 'MOVE', title: op.title, where: `→ ${dayWords(op, today)}` }
    case 'unplace': return { kind: 'MOVE', title: op.title, where: '→ Sometime this week' }
    case 'add': return { kind: 'ADD', title: op.title, where: inArea(op, op.date ? dayWords(op, today) : 'No day yet') }
    case 'someday': return { kind: 'LATER', title: op.title, where: '→ Someday' }
    case 'assign': return { kind: 'WHO', title: op.title, where: `→ ${op.assigneeName ?? 'someone'}` }
    case 'done': return { kind: 'DONE', title: op.title, where: '→ Done' }
    case 'merge': return { kind: 'MERGE', title: `${op.fromTitle ?? 'Another task'} + ${op.title}`, where: '→ one, notes kept' }
    case 'talk': return { kind: 'TALK', title: op.title, where: op.note ? `→ Talk about, ${op.note}` : '→ Talk about' }
    case 'routine_skip': return { kind: 'SKIP', title: op.title, where: `${dayAndDate(op.date!)} only` }
    case 'routine_move': {
      const to = dayAndDate(op.toDate!)
      return { kind: 'MOVE', title: op.title, where: `→ ${op.time ? `${to} ${clock(op.time)}` : to} · ${dayAndDate(op.date!)} only` }
    }
    case 'routine_who': return { kind: 'WHO', title: op.title, where: `→ ${op.assigneeName ?? 'someone'}, ${dayAndDate(op.date!)} only` }
    case 'routine_change': {
      const parts: string[] = []
      const kinds: RowKind[] = []
      if (op.days) { parts.push(daysWords(op.days)); kinds.push('DAYS') }
      if (op.time) { parts.push(clock(op.time)); kinds.push('TIME') }
      if (op.clearTime) { parts.push('any time'); kinds.push('TIME') }
      if (op.assignees) { parts.push(names(op.assigneeNames)); kinds.push('WHO') }
      if (op.showOnTimeline !== undefined) { parts.push(op.showOnTimeline ? 'Show in Today and Week' : 'Hidden from Today and Week · Kept in Routines'); kinds.push('CHANGE') }
      if (op.name) kinds.push('NAME')
      const kind: RowKind = new Set(kinds).size === 1 ? kinds[0] : 'CHANGE'
      const title = op.name ? `${op.title} → ${op.name}` : op.title
      return { kind, title, where: parts.length ? `→ ${parts.join(' ')}` : '' }
    }
    case 'routine_rest': return { kind: 'REST', title: op.title, where: op.until ? `→ until ${ymdDate(op.until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '→ resting' }
    case 'plan_add': return { kind: 'NEW', title: op.title, where: inArea(op, `→ ${op.periodLabel ?? 'the list'}${op.parentTitle ? `, for ${op.parentTitle}` : ''}`) }
    case 'plan_carry': {
      const to = op.toPeriodLabel ?? 'next'
      return { kind: op.toNext === false ? 'MOVE' : 'CARRY', title: op.title, where: op.newTitle ? `→ ${to}, as “${op.newTitle}”` : `→ ${to}` }
    }
    // Let go is "not ever" (letGo.ts): a month or season item comes off
    // every list; a year goal is set aside. Search still finds either.
    case 'plan_let_go': return { kind: 'LET GO', title: op.title, where: op.level === 'year' ? '→ Let go' : '→ Let go · off every list' }
    case 'plan_done': return op.level === 'year' ? { kind: 'MET', title: op.title, where: '→ Met' } : { kind: 'DONE', title: op.title, where: '→ Done' }
    case 'choice_add': return { kind: 'NEW', title: op.choiceTitle ?? op.title, where: '' }
    case 'option_add': return { kind: 'ADD', title: op.optionName ?? op.title, where: op.phone ?? '' }
    case 'option_status': return { kind: 'STATUS', title: op.optionName ?? op.title, where: op.status ? `→ ${STATUS_LABEL[op.status]}` : '' }
    case 'option_set': return { kind: 'SET', title: `${op.optionName ?? op.title} · ${op.columnLabel ?? ''}`, where: cellWords(op.columnKind, op.value) }
    case 'price_line': {
      const labels = op.workLabels ?? []
      return {
        kind: 'PRICE',
        title: `${op.optionName ?? op.title} · ${labels.length ? labels.join(' + ') : 'Price'}${labels.length > 1 ? ', together' : ''}`,
        where: formatCents(op.amountCents ?? 0),
      }
    }
    case 'column_add': return { kind: 'COLUMN', title: op.columnLabel ?? op.title, where: op.columnKind ? `(${KIND_WORD[op.columnKind]})` : '' }
    case 'work_set': return { kind: 'WORK', title: op.workLabels?.[0] ?? op.title, where: op.description ?? '' }
    case 'choice_choose': return { kind: 'CHOOSE', title: op.optionName ?? op.title, where: op.note ? `“${op.note}”` : '' }
    case 'choice_reopen': return { kind: 'REOPEN', title: op.choiceTitle ?? op.title, where: '' }
    case 'follow_up': return { kind: 'FOLLOW UP', title: op.optionName ?? op.title, where: op.date ? dayWords(op, today) : '' }
    // The summary as it will be written (trimmed).
    case 'event_log': return { kind: 'LOG', title: eventTitle(op, logDay(op, today)), where: op.summary?.trim() ? `“${op.summary.trim()}”` : '' }
    case 'event_plan': return { kind: 'PLAN', title: eventTitle(op, op.date ? datedWords(op) : ''), where: 'goes on your calendar' }
    case 'routine_add': {
      const when = [op.days ? daysWords(op.days) : null, op.time ? clock(op.time) : null,
        op.showOnTimeline === undefined ? null : op.showOnTimeline ? '· Show in Today and Week' : '· Hidden from Today and Week · Kept in Routines'].filter(Boolean).join(' ')
      return { kind: 'NEW', title: op.title, where: when ? `→ ${when}` : '' }
    }
  }
}

/** The write for a task op, and what it changes, for Undo. The same writes
 *  Week's own gestures send (drop on a day, back to still to place, Someday). */
export function opWrite(op: ChangeOp, task: Task): { updates: Partial<Task>; previous: Partial<Task> } {
  const before = (updates: Partial<Task>) => ({
    updates,
    previous: Object.fromEntries(Object.keys(updates).map((k) => [k, task[k as keyof Task]])) as Partial<Task>,
  })
  switch (op.op) {
    case 'place': {
      const when = opWhen(op)!
      return before(op.time
        ? { scheduledFor: when, isAllDay: false, bucket: 'timed' }
        : lineDropUpdates(task, { kind: 'day', at: when }))
    }
    case 'unplace': return timingRemoval(task, 'day')
    case 'someday': return before({ bucket: 'someday', scheduledFor: undefined, isAllDay: undefined })
    case 'assign': return before({ assignedTo: op.assignee, assignedToAll: op.assignee ? [op.assignee] : [] })
    case 'talk': {
      // Off its day and onto the talk-about list (here and on the wall).
      const off = task.scheduledFor ? timingRemoval(task, 'day') : { updates: {}, previous: {} }
      const flag: Partial<Task> = { needsDiscussion: true, ...(op.note ? { discussionNote: op.note } : {}) }
      return {
        updates: { ...off.updates, ...flag },
        previous: { ...off.previous, needsDiscussion: task.needsDiscussion ?? false, ...(op.note ? { discussionNote: task.discussionNote } : {}) },
      }
    }
    default: throw new Error(`${op.op} is not a plain task write`)
  }
}
